import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import { omit } from 'lodash/fp';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    type CreatePromoCodeMutation,
    CreatePromoCodeDocument,
    type CreatePromoCodeMutationVariables,
} from '../../../api/mutations/createPromoCode';
import { PromoTypeEnum } from '../../../api/types';
import useLoadingButton from '../../../components/button/useLoadingButton';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import Form from '../../../components/fields/Form';
import NoItemResult from '../../../components/results/NoItemResult';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import useHandleError from '../../../utilities/useHandleError';
import { type NewPromoCodeFormValues, useCreatePromoCodeFormValidator } from '../PromoCodeDetailsPage/PromoCodeForm';
import CreatePromoCodeForm, { defaultValues } from '../PromoCodeDetailsPage/PromoCodeForm/CreatePromoCodeFrom';

const AddPromoCodePage = () => {
    const { t } = useTranslation(['promoCodeDetails']);
    const navigate = useNavigate();
    const validate = useCreatePromoCodeFormValidator();
    const { setLoading, LoadingButton } = useLoadingButton();
    const company = useCompany(true);

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError<NewPromoCodeFormValues>(
        async values => {
            const { moduleId, promoType, moduleVariantsMapping, variantModuleIds, ...settings } = values;
            setLoading(true);
            // submitting message
            message.loading({
                content: t('promoCodeDetails:messages.createSubmitting'),
                key: 'primary',
                duration: 0,
            });

            const newSettings = omit(['startTime', 'endTime'], settings);

            // It's safe to use from company context
            // Because add promo code won't show in all companies
            const newPeriod = {
                start: dayjs(values.period.start)
                    .set('hour', values.startTime.hour())
                    .set('minute', values.startTime.minute())
                    .set('second', 0)
                    .tz(company.timeZone, true)
                    .toDate(),
                end: dayjs(values.period.end)
                    .set('hour', values.endTime.hour())
                    .set('minute', values.endTime.minute())
                    .set('second', 0)
                    .tz(company.timeZone, true)
                    .toDate(),
            };

            // submit creation
            await apolloClient
                .mutate<CreatePromoCodeMutation, CreatePromoCodeMutationVariables>({
                    mutation: CreatePromoCodeDocument,
                    variables: {
                        settings: {
                            ...newSettings,
                            promoType: omit(
                                promoType.type === PromoTypeEnum.Discount ? 'giftPromoType' : 'discountPromoType',
                                promoType
                            ),
                            moduleVariants: (variantModuleIds || []).map(id => ({
                                moduleId: id,
                                addedVariantSuiteIds: moduleVariantsMapping[id] || [],
                            })),
                            period: newPeriod,
                        },
                        moduleId,
                    },
                })
                .finally(() => {
                    setLoading(false);
                    message.destroy('primary');
                });

            // inform about success
            message.success({
                content: t('promoCodeDetails:messages.createSuccessful'),
                key: 'primary',
            });

            // then go back to list
            navigate(`/admin/promoCodes`);
        },
        [apolloClient, company?.timeZone, navigate, setLoading, t]
    );

    if (!company || !company.availableModules.hasPromotionCodeModules) {
        return (
            <ConsolePageWithHeader onBack={() => navigate('/admin/promoCodes')} title={t('promoCodeDetails:addTitle')}>
                <NoItemResult
                    subTitle={
                        company && !company.availableModules.hasLabelsModules
                            ? t('promoCodeDetails:moduleNotAvailable')
                            : t('promoCodeDetails:messages.noCompanySelectedWhenCreate')
                    }
                />
                ;
            </ConsolePageWithHeader>
        );
    }

    return (
        <ConsolePageWithHeader
            footer={[
                <LoadingButton key="submit" form="promoCodeForm" htmlType="submit" type="primary">
                    {t('promoCodeDetails:actions.create')}
                </LoadingButton>,
            ]}
            onBack={() => navigate('/admin/promoCodes')}
            title={t('promoCodeDetails:addTitle')}
        >
            <Formik initialValues={defaultValues} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit }) => (
                    <Form id="promoCodeForm" name="promoCodeForm" onSubmitCapture={handleSubmit}>
                        <CreatePromoCodeForm />
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default AddPromoCodePage;
